/**
 * Brand Tokens Configuration
 *
 * LOCKED to the AI-generated brand guide at docs/design/brand-guide-v1-ai-generated.png.
 * See .impeccable.md at repo root for the full Design Context.
 *
 * Palette anchors (from brand guide, with primary/secondary roles INVERTED
 * from the original brand-guide PNG to make olive the dominant action color):
 * - Primary   #4A5236 (forest/olive) — primaryScale[700]
 *   Used for default CTAs, links, active nav, brand-anchored surfaces.
 * - Secondary #6B1D2B (burgundy)     — secondaryScale[700]
 *   Reserved as a RARE editorial accent: top-tier rating badges, at-capacity
 *   states, and any single-most-important page accent. NOT a default action
 *   color and not a destructive color (use `error` for destructive).
 *
 * Edit this file to change brand styling across the entire application.
 * Changes hot-reload instantly in development — no build step required.
 *
 * NOTE on semantic tokens: keys like `chrome`, `textOnChrome` are retained
 * for API compatibility with the mobile app, but their VALUES are now
 * light-appropriate (paper-layered surfaces, warm-ink text). The old
 * dark-chrome aesthetic was the "I couldn't decide on a theme" anti-pattern
 * called out in .impeccable.md and has been removed.
 */
export const brandTokens = {
    // Font Faces
    // Young Serif (display serif for editorial moments — 400-only on Google Fonts)
    // + Geist (UI sans, variable weights).
    // Loaded via Google Fonts in apps/web/index.html.
    fonts: {
        serif: {
            name: 'Young Serif',
            fallback: ['Georgia', 'Cambria', 'Times New Roman', 'serif'],
            weights: [400],
        },
        sans: {
            name: 'Geist',
            fallback: ['-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Helvetica Neue', 'Arial', 'sans-serif'],
            weights: [300, 400, 500, 600, 700],
        },
    },
    // Typography Scale
    // Display headings (h1, h2) use Young Serif at 400 weight — the face is
    // 400-only and carries presence without bold. Structural headings (h3–h5)
    // use Geist semibold for operational clarity. Body / small / caption
    // default to Geist at 400.
    typography: {
        h1: { fontFamily: 'serif', fontSize: '2.25rem', fontWeight: 400, lineHeight: 1.25 },
        h2: { fontFamily: 'serif', fontSize: '1.875rem', fontWeight: 400, lineHeight: 1.25 },
        h3: { fontFamily: 'sans', fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25 },
        h4: { fontFamily: 'sans', fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.25 },
        h5: { fontFamily: 'sans', fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25 },
        p: { fontFamily: 'sans', fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
        small: { fontFamily: 'sans', fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
        caption: { fontFamily: 'sans', fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5 },
    },
    // Color Palette
    colors: {
        // Single-value semantic colors
        warning: '#f59e0b', // Gold — wine-adjacent, kept as-is
        error: '#C94938', // Warm crimson — hue well away from burgundy primary (resolves red-on-red confusion)
        caution: '#fbbf24', // Yellow — kept as-is
        // primaryScale: FOREST / OLIVE — anchored at #4A5236 (700).
        // The dominant action color across the app: default CTAs, links, active
        // nav, hovers, chips/badges. Promoted to primary because burgundy as a
        // ubiquitous action color read as "alert" rather than "brand."
        primaryScale: {
            50: '#F6F7F1',
            100: '#E8EADD',
            200: '#D1D5BB',
            300: '#A5AE85',
            400: '#7A864D',
            500: '#5E6A3B',
            600: '#525930',
            700: '#4A5236', // locked brand guide olive anchor
            800: '#3B422A',
            900: '#2A301E',
        },
        // secondaryScale: BURGUNDY — anchored at #6B1D2B (700).
        // RARE editorial accent. Reserve for: top-tier rating badges
        // (Extraordinary), at-capacity / urgent states, and the single most
        // important hero accent on a given page. NOT a default action color.
        secondaryScale: {
            50: '#FDF6F7',
            100: '#F5E0E3',
            200: '#E5B2B9',
            300: '#D58590',
            400: '#B85567',
            500: '#8F3749',
            600: '#7E2738',
            700: '#6B1D2B', // locked brand guide burgundy anchor
            800: '#5B1925',
            900: '#461219',
        },
        // tertiaryScale: DUSTY TERRACOTTA — wine-adjacent earth tone.
        // Key retained for API compatibility with mobile code (uses tertiaryScale[600]);
        // values re-aimed OFF the original purple which was off-brand.
        // Use sparingly — this is not a third primary.
        tertiaryScale: {
            50: '#FBF3EE',
            100: '#F4DDD0',
            200: '#E8BFA8',
            300: '#D99E80',
            400: '#C47E5E',
            500: '#B56A49',
            600: '#A05540',
            700: '#844337',
            800: '#66332A',
            900: '#472320',
        },
        // gray: WARM-TINTED NEUTRALS (hue ~80, very low chroma).
        // 50 is the ground — nearly-white with a whisper of warmth, perceptible
        // side-by-side with #FFFFFF but not "vintage paper." 900 is the warm
        // near-black ink.
        gray: {
            50: '#FAF9F6', // ground — nearly-white, subtle warmth
            100: '#F2F0EA', // layered surface — barely-there paper hierarchy
            200: '#DBD4C9',
            300: '#BDB5AA',
            400: '#91897F',
            500: '#6F6C66',
            600: '#54524F',
            700: '#3E3D3B',
            800: '#292827',
            900: '#1B1B1A', // warm near-black ink
        },
    },
    // Semantic Color Tokens
    // Keys preserved for mobile API compatibility; values re-pointed for LIGHT mode.
    semantic: {
        background: '#FAF9F6', // gray.50 — nearly-white ground (screen bg)
        surface: '#FAF9F6', // gray.50 — no stark-white cards; same ground
        surfaceSecondary: '#F2F0EA', // gray.100 — subtly layered surface for hierarchy
        chrome: '#F2F0EA', // gray.100 — paper-layered tab bars / headers (was dark navy)
        textPrimary: '#1B1B1A', // gray.900 — warm near-black ink
        textSecondary: '#3E3D3B', // gray.700 — warm dark gray
        textMuted: '#6F6C66', // gray.500 — warm muted
        textOnChrome: '#1B1B1A', // gray.900 — warm ink (chrome is now light, so text is dark)
        textOnChromeMuted: '#54524F', // gray.600 — warm dark muted
        // For text sitting on a DARK brand surface — the olive auth gradient, a
        // photo scrim, any primaryScale[700..900] ground. Distinct from
        // textOnChrome, which since the dark-navy-to-light-paper swap means ink on
        // light chrome and reads dark-on-dark if borrowed for these.
        textOnDark: '#FAF9F6', // gray.50 — near-white ground colour, ≥7:1 on olive 700–900
        textOnDarkMuted: '#DBD4C9', // gray.200 — warm light gray, ≥5.5:1 on olive 700–900
        borderDefault: '#DBD4C9', // gray.200 — warm light gray
        borderSubtle: '#F2F0EA', // gray.100 — subtle paper divider
    },
    // Spacing System
    spacing: {
        base: 6, // Base unit in pixels (all spacing is multiples of this)
        scale: {
            0: 0,
            1: 1, // 6px
            2: 2, // 12px
            3: 3, // 18px
            4: 4, // 24px
            5: 5, // 30px
            6: 6, // 36px
            8: 8, // 48px
            10: 10, // 60px
            12: 12, // 72px
            16: 16, // 96px
        },
    },
    // Button Styles
    button: {
        borderRadius: 2, // Multiple of base spacing (12px)
        padding: {
            sm: { x: 3, y: 1.5 }, // 18px x 9px
            md: { x: 4, y: 2 }, // 24px x 12px
            lg: { x: 5, y: 2.5 }, // 30px x 15px
        },
        borderWidth: 1, // pixels
        borderColor: {
            primary: '#4A5236', // olive primary anchor (default action color)
            secondary: '#6B1D2B', // burgundy secondary anchor (rare accent only)
            default: '#BDB5AA', // gray.300 warm border
        },
    },
};
